import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, apiError, fmtDate } from "../lib/api";
import { Card, Empty, Sheet, Skeleton } from "../components/ui";
import { DayReportView } from "./PollReportPage";
import { FullScorecard, Scoreboard } from "./ScoreLivePage";
import { BarChart3 } from "lucide-react";
import HmtBanner from "../components/HmtBanner";

/** Read-only match-day report opened from the WhatsApp link — no login needed. */
export default function PublicDayReportPage() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let timer;
    const load = async () => {
      try {
        const { data } = await api.get(`/public/match-day/${id}`);
        setR(data);
        // Keep refreshing while a match is still being scored
        if (data.matches.some((m) => m.status !== "completed")) timer = setTimeout(load, 20000);
      } catch (err) {
        setError(apiError(err, "Couldn't load this match day"));
      }
    };
    load();
    return () => clearTimeout(timer);
  }, [id]);

  const open = r?.scorecards.find((s) => s.id === openId);

  return (
    <div className="app-shell">
      <div className="min-h-screen pb-12">
        <HmtBanner />
        <div className="px-5 pt-6 pb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            {r && (
              <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold truncate">
                {[r.club?.name, fmtDate(r.poll.date), r.poll.ground].filter(Boolean).join(" • ")}
              </div>
            )}
            <h1 className="text-2xl font-bold text-ink leading-tight">{r?.poll.title || "Match day"}</h1>
          </div>
          <img src="/logo.png" alt="Cricso" className="h-10 w-10 object-contain shrink-0" />
        </div>

        {error ? (
          <div className="px-5">
            <Card className="p-0"><Empty icon={BarChart3} title="Not available" subtitle={error} /></Card>
          </div>
        ) : !r ? (
          <div className="px-5 space-y-3">
            <Skeleton className="h-20" />
            <Skeleton className="h-40" />
            <Skeleton className="h-64" />
          </div>
        ) : (
          <DayReportView r={r} onOpenMatch={(m) => setOpenId(m.id)} />
        )}

        <div className="text-center text-xs text-ink-muted mt-8">
          Scored live with <Link to="/login" className="font-semibold text-brand">Cricso</Link>
        </div>
      </div>

      <Sheet open={!!open} onClose={() => setOpenId(null)} title={open?.title || ""}>
        {open && (
          <div className="space-y-4">
            <Scoreboard sc={open} />
            <FullScorecard sc={open} />
          </div>
        )}
      </Sheet>
    </div>
  );
}
